import { keysToCamelCase } from './../../../shared/utils/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { uuidv4 } from '@shared/utils/utilsOld';
import { PaginatedList, RequestStatusEnum } from '@shared/types';
import { AxiosError, AxiosResponse } from 'axios';
import {
    AutoLinkPattern,
    Defect,
    DefectFormValues,
    DefectsMultipleActionParams,
    EditDefectParams,
    GetDefectParams,
    JiraIssue,
} from '../types';

export type DefectsState = {
    getDefectsStatus: RequestStatusEnum;
    getDefectsError: PayloadAction<AxiosError> | null;
    defects: Defect[];
    defectsCount: number;
    deleteDefectsStatus: RequestStatusEnum;
    deleteDefectsError: PayloadAction<AxiosError> | null;
    closeDefectsStatus: RequestStatusEnum;
    closeDefectsError: PayloadAction<AxiosError> | null;
    addDefectStatus: RequestStatusEnum;
    addDefectError: PayloadAction<AxiosError> | null;
    deleteDefectStatus: RequestStatusEnum;
    deleteDefectError: PayloadAction<AxiosError> | null;
    editDefectStatus: RequestStatusEnum;
    editDefectError: PayloadAction<AxiosError> | null;
    getDefectStatus: RequestStatusEnum;
    getDefectError: PayloadAction<AxiosError> | null;
    defect: Defect | null;
    getDefectsForSelectorStatus: RequestStatusEnum;
    getDefectsForSelectorError: PayloadAction<AxiosError> | null;
    defectsForSelector: Defect[];
    defectsForSelectorCount: number;
    getJiraIssuesStatus: RequestStatusEnum;
    getJiraIssuesError: PayloadAction<AxiosError> | null;
    jiraIssues: JiraIssue[];
    jiraIssuesCount: number;
    createdDefect: Defect | null;
};

const initialState: DefectsState = {
    getDefectsStatus: RequestStatusEnum.NotRequested,
    getDefectsError: null,
    defects: [],
    defectsCount: 0,
    deleteDefectsStatus: RequestStatusEnum.NotRequested,
    deleteDefectsError: null,
    closeDefectsStatus: RequestStatusEnum.NotRequested,
    closeDefectsError: null,
    addDefectStatus: RequestStatusEnum.NotRequested,
    addDefectError: null,
    deleteDefectStatus: RequestStatusEnum.NotRequested,
    deleteDefectError: null,
    editDefectStatus: RequestStatusEnum.NotRequested,
    editDefectError: null,
    getDefectStatus: RequestStatusEnum.NotRequested,
    getDefectError: null,
    defect: null,
    getDefectsForSelectorStatus: RequestStatusEnum.NotRequested,
    getDefectsForSelectorError: null,
    defectsForSelector: [],
    defectsForSelectorCount: 0,
    getJiraIssuesStatus: RequestStatusEnum.NotRequested,
    getJiraIssuesError: null,
    jiraIssues: [],
    jiraIssuesCount: 0,
    createdDefect: null,
};

const defectsSlice = createSlice({
    name: '@defects',
    initialState,
    reducers: {
        getDefectsRequest(state) {
            state.getDefectsStatus = RequestStatusEnum.Loading;
            state.getDefectsError = null;
            state.defects = [];
            state.defectsCount = 0;
        },
        getDefectsSuccess(state, action) {
            state.getDefectsStatus = RequestStatusEnum.Success;
            state.getDefectsError = null;
            state.defects = action.payload.data.items.map((defect: Defect) => keysToCamelCase(defect));
            state.defectsCount = action.payload.data.count;
        },
        getDefectsError(state, action) {
            state.getDefectsStatus = RequestStatusEnum.Error;
            state.getDefectsError = action.payload;
            state.defects = [];
            state.defectsCount = 0;
        },
        getDefectsReset(state) {
            state.getDefectsStatus = RequestStatusEnum.NotRequested;
            state.getDefectsError = null;
            state.defects = [];
            state.defectsCount = 0;
        },
        deleteDefectsRequest(state, _action: PayloadAction<DefectsMultipleActionParams>) {
            state.deleteDefectsStatus = RequestStatusEnum.Loading;
            state.deleteDefectsError = null;
        },
        deleteDefectsSuccess(state, _action) {
            state.deleteDefectsStatus = RequestStatusEnum.Success;
            state.deleteDefectsError = null;
        },
        deleteDefectsError(state, action) {
            state.deleteDefectsStatus = RequestStatusEnum.Error;
            state.deleteDefectsError = action.payload;
        },
        deleteDefectsReset(state) {
            state.deleteDefectsStatus = RequestStatusEnum.NotRequested;
            state.deleteDefectsError = null;
        },
        closeDefectsRequest(state, _action: PayloadAction<DefectsMultipleActionParams>) {
            state.closeDefectsStatus = RequestStatusEnum.Loading;
            state.closeDefectsError = null;
        },
        closeDefectsSuccess(state, _action) {
            state.closeDefectsStatus = RequestStatusEnum.Success;
            state.closeDefectsError = null;
        },
        closeDefectsError(state, action) {
            state.closeDefectsStatus = RequestStatusEnum.Error;
            state.closeDefectsError = action.payload;
        },
        closeDefectsReset(state) {
            state.closeDefectsStatus = RequestStatusEnum.NotRequested;
            state.closeDefectsError = null;
        },
        addDefectRequest(state, _action: PayloadAction<DefectFormValues>) {
            state.addDefectStatus = RequestStatusEnum.Loading;
            state.addDefectError = null;
        },
        addDefectSuccess(state, action: PayloadAction<Defect>) {
            state.addDefectStatus = RequestStatusEnum.Success;
            state.addDefectError = null;
            state.createdDefect = action.payload;
        },
        addDefectError(state, action) {
            state.addDefectStatus = RequestStatusEnum.Error;
            state.addDefectError = action.payload;
        },
        addDefectReset(state) {
            state.addDefectStatus = RequestStatusEnum.NotRequested;
            state.addDefectError = null;
        },
        getDefectRequest(state, _action: PayloadAction<GetDefectParams>) {
            state.getDefectStatus = RequestStatusEnum.Loading;
            state.getDefectError = null;
            state.defect = null;
        },
        getDefectSuccess(state, action) {
            state.getDefectStatus = RequestStatusEnum.Success;
            state.getDefectError = null;
            state.defect = {
                ...action.payload,
                autoLinkPatterns: action.payload.autoLinkPatterns.map((pattern: AutoLinkPattern) => ({
                    ...pattern,
                    pk: uuidv4(),
                })),
            };
        },
        getDefectError(state, action) {
            state.getDefectStatus = RequestStatusEnum.Error;
            state.getDefectError = action.payload;
            state.defect = null;
        },
        getDefectReset(state) {
            state.getDefectStatus = RequestStatusEnum.NotRequested;
            state.getDefectError = null;
            state.defect = null;
        },
        editDefectRequest(state, _action: PayloadAction<EditDefectParams>) {
            state.editDefectStatus = RequestStatusEnum.Loading;
            state.editDefectError = null;
        },
        editDefectSuccess(state, action) {
            state.editDefectStatus = RequestStatusEnum.Success;
            state.editDefectError = null;
            const updatedDefect = keysToCamelCase(action.payload);
            state.defect = updatedDefect;
            const index = state.defects.findIndex((defect) => defect.pk === updatedDefect.pk);
            if (index !== -1) {
                state.defects[index] = updatedDefect;
            }
        },
        editDefectError(state, action) {
            state.editDefectStatus = RequestStatusEnum.Error;
            state.editDefectError = action.payload;
        },
        editDefectReset(state) {
            state.editDefectStatus = RequestStatusEnum.NotRequested;
            state.editDefectError = null;
        },
        deleteDefectRequest(state, _action) {
            state.deleteDefectStatus = RequestStatusEnum.Loading;
            state.deleteDefectError = null;
        },
        deleteDefectSuccess(state) {
            state.deleteDefectStatus = RequestStatusEnum.Success;
            state.deleteDefectError = null;
        },
        deleteDefectError(state, action) {
            state.deleteDefectStatus = RequestStatusEnum.Error;
            state.deleteDefectError = action.payload;
        },
        deleteDefectReset(state) {
            state.deleteDefectStatus = RequestStatusEnum.NotRequested;
            state.deleteDefectError = null;
        },
        getDefectsForSelectorRequest(state) {
            state.getDefectsForSelectorStatus = RequestStatusEnum.Loading;
            state.getDefectsForSelectorError = null;
        },
        getDefectsForSelectorSuccess(state, action: PayloadAction<AxiosResponse<PaginatedList<Defect>>>) {
            state.getDefectsForSelectorStatus = RequestStatusEnum.Success;
            const existingIds = new Set(state.defectsForSelector.map((d) => d.pk));
            const newItems = action.payload.data.items.filter((d: Defect) => !existingIds.has(d.pk));
            state.defectsForSelector = [...state.defectsForSelector, ...newItems].map((defect) =>
                keysToCamelCase(defect),
            );
            state.defectsForSelectorCount = action.payload.data.count;
        },
        getDefectsForSelectorError(state, action: PayloadAction<AxiosError>) {
            state.getDefectsForSelectorStatus = RequestStatusEnum.Error;
            state.getDefectsForSelectorError = action;
        },
        getDefectsForSelectorReset(state) {
            state.getDefectsForSelectorStatus = RequestStatusEnum.NotRequested;
            state.getDefectsForSelectorError = null;
            state.defectsForSelector = [];
            state.defectsForSelectorCount = 0;
        },
        resetArrayDefectsForSelector(state) {
            state.defectsForSelector = [];
            state.defectsForSelectorCount = 0;
        },
        getJiraIssuesRequest(state) {
            state.getJiraIssuesStatus = RequestStatusEnum.Loading;
            state.getJiraIssuesError = null;
        },
        getJiraIssuesSuccess(state, action: PayloadAction<AxiosResponse<PaginatedList<JiraIssue>>>) {
            state.getJiraIssuesStatus = RequestStatusEnum.Success;
            state.jiraIssues = [
                ...state.jiraIssues,
                ...action.payload.data.items.map((issue) => keysToCamelCase(issue)),
            ];
            state.jiraIssuesCount = action.payload.data.count;
        },
        getJiraIssuesError(state, action: PayloadAction<AxiosError>) {
            state.getJiraIssuesStatus = RequestStatusEnum.Error;
            state.getJiraIssuesError = action;
        },
        getJiraIssuesReset(state) {
            state.getJiraIssuesStatus = RequestStatusEnum.NotRequested;
            state.getJiraIssuesError = null;
            state.jiraIssues = [];
            state.jiraIssuesCount = 0;
        },
        resetArrayJiraIssues(state) {
            state.jiraIssues = [];
            state.jiraIssuesCount = 0;
        },
    },
});

export default defectsSlice.reducer;

export const {
    getDefectsRequest,
    getDefectsSuccess,
    getDefectsError,
    getDefectsReset,
    deleteDefectsRequest,
    deleteDefectsSuccess,
    deleteDefectsError,
    deleteDefectsReset,
    closeDefectsRequest,
    closeDefectsSuccess,
    closeDefectsError,
    closeDefectsReset,
    addDefectRequest,
    addDefectSuccess,
    addDefectError,
    addDefectReset,
    getDefectRequest,
    getDefectSuccess,
    getDefectError,
    getDefectReset,
    editDefectRequest,
    editDefectSuccess,
    editDefectError,
    editDefectReset,
    deleteDefectRequest,
    deleteDefectSuccess,
    deleteDefectError,
    deleteDefectReset,
    getDefectsForSelectorRequest,
    getDefectsForSelectorSuccess,
    getDefectsForSelectorError,
    getDefectsForSelectorReset,
    resetArrayDefectsForSelector,
    getJiraIssuesRequest,
    getJiraIssuesSuccess,
    getJiraIssuesError,
    getJiraIssuesReset,
    resetArrayJiraIssues,
} = defectsSlice.actions;
